import { useEffect, useRef } from 'react';

/* ─── tunables ─────────────────────────────────────────────── */
const DOT_COLOR   = '100, 255, 218';   // teal accent
const LINE_COLOR  = '100, 255, 218';
const DOT_ALPHA   = 0.55;              // star brightness
const LINE_ALPHA  = 0.12;             // connection opacity (subtle)
const LINK_DIST   = 140;              // px — max distance to draw a line
const LINK_DIST_SQ = LINK_DIST * LINK_DIST; // compare squared distances (no sqrt)
const SPEED       = 0.10;             // px per frame
const DOT_RADIUS  = 1.6;             // star size
/* ─────────────────────────────────────────────────────────── */

function particleCount(w, h) {
  const area = w * h;
  // ~1 particle per 14 000 px², capped for mobile perf
  return Math.min(Math.floor(area / 14000), 90);
}

function makeParticle(w, h) {
  const angle = Math.random() * Math.PI * 2;
  const speed = (0.4 + Math.random() * 0.6) * SPEED;
  return {
    x:  Math.random() * w,
    y:  Math.random() * h,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    r:  DOT_RADIUS * (0.7 + Math.random() * 0.6),
    a:  DOT_ALPHA  * (0.5 + Math.random() * 0.5),
  };
}

export default function Constellation() {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Skip the decorative canvas on touch/mobile — saves continuous
    // main-thread + GPU work on the devices that need it most.
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    let   rafId  = null;
    let   particles = [];
    let   onScreen = true; // updated by IntersectionObserver

    function resize() {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      const n = particleCount(canvas.width, canvas.height);
      // Keep existing particles, add/remove to hit target count
      while (particles.length < n) particles.push(makeParticle(canvas.width, canvas.height));
      if (particles.length > n)   particles = particles.slice(0, n);
    }

    function draw() {
      const { width: W, height: H } = canvas;
      ctx.clearRect(0, 0, W, H);

      // Move & wrap
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
        if (p.y < -10) p.y = H + 10;
        if (p.y > H + 10) p.y = -10;
      }

      // Draw lines between close pairs — squared-distance test avoids sqrt
      // for the vast majority of pairs that are out of range.
      ctx.lineWidth = 0.8;
      for (let i = 0; i < particles.length; i++) {
        const pi = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const pj = particles[j];
          const dx = pi.x - pj.x;
          const dy = pi.y - pj.y;
          const distSq = dx * dx + dy * dy;
          if (distSq < LINK_DIST_SQ) {
            // sqrt only computed for pairs actually connected
            const dist  = Math.sqrt(distSq);
            const alpha = LINE_ALPHA * (1 - dist / LINK_DIST);
            ctx.beginPath();
            ctx.moveTo(pi.x, pi.y);
            ctx.lineTo(pj.x, pj.y);
            ctx.strokeStyle = `rgba(${LINE_COLOR}, ${alpha.toFixed(3)})`;
            ctx.stroke();
          }
        }
      }

      // Draw dots (stars)
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${DOT_COLOR}, ${p.a.toFixed(3)})`;
        ctx.fill();
      }

      rafId = requestAnimationFrame(draw);
    }

    /* ── Loop lifecycle: only run when visible AND on-screen ── */
    function isActive() {
      return onScreen && document.visibilityState === 'visible';
    }
    function start() {
      if (rafId === null && isActive()) rafId = requestAnimationFrame(draw);
    }
    function stop() {
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
    }
    function syncRunState() {
      if (isActive()) start();
      else stop();
    }

    resize();
    start();

    const onResize = () => { resize(); };
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', syncRunState);

    // Pause the RAF loop when the canvas scrolls out of the viewport
    const io = new IntersectionObserver(
      ([entry]) => { onScreen = entry.isIntersecting; syncRunState(); },
      { threshold: 0 }
    );
    io.observe(canvas);

    return () => {
      stop();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', syncRunState);
      io.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position:      'fixed',
        inset:         0,
        width:         '100%',
        height:        '100%',
        pointerEvents: 'none',
        zIndex:        0,
        opacity:       1,
      }}
    />
  );
}
